// App config
//

import React, { useState, useMemo } from 'react'
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
} from 'react-router-dom'
import {
  ThemeProvider,
  CssBaseline,
  Container,
  Typography,
} from '@mui/material'
import { AuthProvider } from './context/AuthContext'
import { CrudProvider } from './context/CrudContext'
import { SocketProvider } from './context/SocketContext'
import theme from './theme'
import useAuth from './hooks/useAuth'

// Layouts
import PublicLayout from './layout/PublicLayout'
import DefaultLayout from './layout/DefaultLayout'

// Páginas públicas
import Home from './pages/home/Home'
import Login from './pages/login/Login'
import Register from './pages/register/Register'

// Páginas privadas
import Dashboard from './pages/dashboard/Dashboard'
import Clients from './pages/clients/Clients'
import ClientDetail from './pages/clients/ClientDetail'
import Products from './pages/products/Products'
import ProductDetail from './pages/products/ProductDetail'
import ConsumerSearch from './pages/consumers/ConsumerSearch'
import Users from './pages/users/Users'
import UserDetail from './pages/users/UserDetail'
import CalendarPage from './pages/calendar/Calendar'
import Booking from './pages/booking/Booking'
import Chat from './pages/chat/Chat'

// Settings
import Settings from './pages/settings/Settings'
import ControlPanel from './pages/settings/ControlPanel'
import LayoutEditor from './pages/settings/LayoutEditor'
import ProductLayoutManager from './pages/settings/ProductLayoutManager'
import OperationalContextManager from './pages/settings/OperationalContextManager'
import ProductLayoutPreview from './pages/settings/ProductsLayoutPreview'
import ActionResultCodes from './pages/settings/ActionResultCodes' /* Legacy operational route kept temporarily for backward compatibility.
    Do not use as administrative catalog management entry point. */
import CodesManagement from './pages/consumers/CodesManagement'
import ConsumerWizard from './pages/settings/ConsumerWizard'
import ConsumerBulkUpload from './pages/settings/ConsumerBulkUpload'
import WorkflowWidgetsConfig from './pages/settings/WorkflowWidgetsConfig'

// Otras
import Page404 from './pages/page404/Page404'

// Placeholder simple para rutas aún no implementadas (evita 404)
const ComingSoon = ({ title }) => (
  <Container sx={{ mt: 3 }}>
    <Typography variant="h5">{title}</Typography>
    <Typography variant="body2" sx={{ mt: 1 }}>
      Módulo pendiente de estabilización / implementación.
    </Typography>
  </Container>
)

// Ruta privada
const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth()
  const navigate = useNavigate()

  React.useEffect(() => {
    if (!user && !loading) {
      navigate('/login')
    }
    // eslint-disable-next-line
  }, [user, loading])

  if (loading) {
    return (
      <div style={{ textAlign: 'center', marginTop: '20px' }}>Cargando...</div>
    )
  }

  return user ? children : <Navigate to="/login" replace />
}

function AppWrapper() {
  const [themeMode, setThemeMode] = useState('light')
  const currentTheme = useMemo(() => theme(themeMode), [themeMode])
  const [isSidebarVisible, setIsSidebarVisible] = useState(true)

  const toggleTheme = () =>
    setThemeMode((prev) => (prev === 'light' ? 'dark' : 'light'))
  const toggleSidebar = () => setIsSidebarVisible((prev) => !prev)

  return (
    <ThemeProvider theme={currentTheme}>
      <CssBaseline enableColorScheme />
      <AuthProvider>
        <SocketProvider>
          <CrudProvider>
            <Routes>
              {/* Públicas */}
              <Route
                path="/"
                element={
                  <PublicLayout>
                    <Home />
                  </PublicLayout>
                }
              />
              <Route
                path="/login"
                element={
                  <PublicLayout>
                    <Login />
                  </PublicLayout>
                }
              />
              <Route
                path="/register"
                element={
                  <PublicLayout>
                    <Register />
                  </PublicLayout>
                }
              />
              <Route
                path="/booking/:token"
                element={
                  <PublicLayout>
                    <Booking />
                  </PublicLayout>
                }
              />

              {/* Privadas dentro de DefaultLayout */}
              <Route
                element={
                  <PrivateRoute>
                    <DefaultLayout
                      isSidebarVisible={isSidebarVisible}
                      toggleSidebar={toggleSidebar}
                      toggleTheme={toggleTheme}
                      themeMode={themeMode}
                    />
                  </PrivateRoute>
                }
              >
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/clients" element={<Clients />} />
                <Route path="/clients/:clientId" element={<ClientDetail />} />
                <Route path="/products" element={<Products />} />
                <Route path="/products/:productId" element={<ProductDetail />} />
                <Route
                  path="/products/:productId/consumers"
                  element={<ConsumerSearch />}
                />
                <Route path="/users" element={<Users />} />
                <Route path="/users/:userId" element={<UserDetail />} />
                <Route path="/calendar" element={<CalendarPage />} />
                <Route path="/chat" element={<Chat />} />

                {/* Configuración */}
                <Route path="/settings" element={<Settings />}>
                  <Route
                    index
                    element={<Navigate to="controlpanel" replace />}
                  />
                  <Route
                    path="workflow-widgets"
                    element={<WorkflowWidgetsConfig />}
                  />

                  <Route
                    path="workflow-widgets"
                    element={<WorkflowWidgetsConfig />}
                  />

                  <Route path="controlpanel" element={<ControlPanel />}>
                    {/* rutas reales hijas del ControlPanel */}
                    <Route
                      path="layout-manager"
                      element={<ProductLayoutManager />}
                    />
                    <Route
                      path="operational-context"
                      element={<OperationalContextManager />}
                    />
                    <Route
                      path="products/:productId/layouts"
                      element={<ProductLayoutManager />}
                    />
                    <Route
                      path="preview/:layoutId"
                      element={<ProductLayoutPreview />}
                    />
                    <Route
                      path="action-result-codes"
                      element={<ActionResultCodes />}
                    />
                    <Route
                      path="codes-management"
                      element={<CodesManagement />}
                    />

                    {/* editor: requiere productId explícito */}
                    <Route
                      path="products/:productId/layout-editor"
                      element={<LayoutEditor />}
                    />
                    <Route
                      path="products/:productId/layout-editor/:layoutId"
                      element={<LayoutEditor />}
                    />

                    {/* placeholders para evitar 404 si aún no existen pantallas */}
                    <Route
                      path="users"
                      element={<ComingSoon title="Usuarios" />}
                    />
                    <Route
                      path="roles"
                      element={<ComingSoon title="Roles y privilegios" />}
                    />
                    <Route
                      path="uploads"
                      element={<ComingSoon title="Cargas / Auditoría" />}
                    />
                  </Route>
                </Route>

                {/* Wizards y carga masiva de consumidores */}
                <Route
                  path="/settings/consumers/create"
                  element={<ConsumerWizard />}
                />
                <Route
                  path="/settings/consumers/bulk"
                  element={<ConsumerBulkUpload />}
                />
              </Route>

              {/* 404 */}
              <Route path="*" element={<Page404 />} />
            </Routes>
          </CrudProvider>
        </SocketProvider>
      </AuthProvider>
    </ThemeProvider>
  )
}

export default function App() {
  return (
    <Router>
      <AppWrapper />
    </Router>
  )
}
